import {
  ALL_PRODUCT_TYPES,
  BLACK_HEX,
  DEFAULT_CUTOFF_VALUE,
  DEFAULT_DOT_OPACITY,
  DEFAULT_LINE_THICKNESS,
  DEFAULT_MATERIAL_BALANCE_LINE_THICKNESS,
  DEFAULT_NORMALIZATION_UNITS,
  DEFAULT_NORMALIZE_BY_DISPLAY_FIELD,
  DEFAULT_NORMALIZE_BY_FIELD,
  DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
  DEFAULT_NORMALIZE_BY_LOWER_SCALER,
  DEFAULT_NORMALIZE_BY_PER,
  DEFAULT_NORMALIZE_BY_THRESHOLD,
  DEFAULT_NORMALIZE_BY_UNIT,
  DEFAULT_OPACITY,
  DEFAULT_POINT_SIZE,
  DEFAULT_SHUT_IN_MONTHS,
  ORIGIN
} from "constants/chart.constants";
import { CalculationMethod, EvaSource, TimeStep } from "constants/charts.enums";
import { createStateWithDefaults } from "entities/utils";
import {
  BestFitToggleFeature,
  BoxPlotBinsFeature,
  CalculationMethodFeature,
  ChartBaseFeature,
  ChartFocusFeature,
  ChartTypeParamFeature,
  CutOffFeature,
  DataRangeSelectFeature,
  DataTableFeature,
  HighlightedLineOpacityFeature,
  HighlightedLineSizeFeature,
  JitterPlotToggleFeature,
  LassoFeature,
  LineOpacityFeature,
  LineSizeFeature,
  LogAxisFeature,
  MovingAverageDaysFeature,
  MultiProductFeature,
  MultiProductFocusFeature,
  NormalizationFeature,
  PointOpacityFeature,
  PointSizeFeature,
  PressureChartSettingsFeature,
  ScreenshotFeature,
  ShutInMonthsFeature,
  SlopeLineFeature,
  SourceFeature,
  StatisticsFeature,
  TimeStepFeature,
  TotalFluidChartSettingsFeature,
  ViolinPlotToggleFeature,
  XAxisSortFeature
} from "types";

import { RegressionType } from "models/regressionType";

import { DEFAULT_COLOR_PALETTE_NAME } from "../../../../components/user-settings/models";
import { EntityKind } from "../../../../models/entityKind";
import { PdenSourceEnum } from "../../../../models/pdenDataSourceSetting";
import { DEFAULT_WIDTH, HIGHLIGHTED_WIDTH } from "../translators/constants";

export const DEFAULT_FEATURE_ENTITY: Partial<ChartBaseFeature> = {
  properties: {},
  show: false,
  active: false,
  enabled: false
};

export const generateChartsFeatureEntity =
  createStateWithDefaults(DEFAULT_FEATURE_ENTITY);

export const CHART_DATA_TABLE_LABEL = "Show Raw Data";
export const DEFAULT_DATA_TABLE_FEATURE_ENTITY: Partial<DataTableFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    tooltipText: CHART_DATA_TABLE_LABEL,
    overflowLabel: CHART_DATA_TABLE_LABEL
  }
};

export const generateDataTableFeatureEntity = createStateWithDefaults(
  DEFAULT_DATA_TABLE_FEATURE_ENTITY
);

export const DEFAULT_SLOPE_LINE_FEATURE_ENTITY: Partial<SlopeLineFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    colour: BLACK_HEX,
    thickness: DEFAULT_MATERIAL_BALANCE_LINE_THICKNESS,
    length: 50,
    position: null
  }
};

export const generateSlopeLineFeatureEntity = createStateWithDefaults(
  DEFAULT_SLOPE_LINE_FEATURE_ENTITY
);
export const DEFAULT_SOURCE_FEATURE_ENTITY: Partial<SourceFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: EvaSource.Public,
    // Must be explicitly undefined so lodash _has() finds the path.
    // updateChartByPath uses _has() as a guard and silently no-ops if the
    // path doesn't exist in the initial state. gDC == 0, so guard with != null.
    specificPublicSource: undefined
  }
};

export const generateSourceFeatureEntity = createStateWithDefaults(
  DEFAULT_SOURCE_FEATURE_ENTITY
);

export const DEFAULT_TIME_STEP_FEATURE_ENTITY: Partial<TimeStepFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: TimeStep.Month
  }
};

export const generateTimeStepFeatureEntity = createStateWithDefaults(
  DEFAULT_TIME_STEP_FEATURE_ENTITY
);

export const DEFAULT_CUT_OFF_FEATURE_ENTITY: Partial<CutOffFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_CUTOFF_VALUE
  }
};

export const generateCutOffFeatureEntity = createStateWithDefaults(
  DEFAULT_CUT_OFF_FEATURE_ENTITY
);

export const DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY: Partial<ShutInMonthsFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_SHUT_IN_MONTHS
  }
};

export const generateShutInMonthsFeatureEntity = createStateWithDefaults(
  DEFAULT_SHUT_IN_MONTHS_FEATURE_ENTITY
);
export const DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY: Partial<MovingAverageDaysFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      value: 0
    }
  };

export const generateMovingAverageDaysFeatureEntity = createStateWithDefaults(
  DEFAULT_MOVING_AVERAGE_DAYS_FEATURE_ENTITY
);

export const DEFAULT_POINT_OPACITY_FEATURE_ENTITY: Partial<PointOpacityFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_DOT_OPACITY
  }
};

export const generatePointOpacityFeatureEntity = createStateWithDefaults(
  DEFAULT_POINT_OPACITY_FEATURE_ENTITY
);

export const DEFAULT_POINT_SIZE_FEATURE_ENTITY: Partial<PointSizeFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_POINT_SIZE
  }
};

export const generatePointSizeFeatureEntity = createStateWithDefaults(
  DEFAULT_POINT_SIZE_FEATURE_ENTITY
);

export const DEFAULT_LINE_SIZE_FEATURE_ENTITY: Partial<LineSizeFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_WIDTH
  }
};

export const generateLineSizeFeatureEntity = createStateWithDefaults(
  DEFAULT_LINE_SIZE_FEATURE_ENTITY
);

export const DEFAULT_LINE_OPACITY_FEATURE_ENTITY: Partial<LineOpacityFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: DEFAULT_OPACITY
  }
};

export const generateLineOpacityFeatureEntity = createStateWithDefaults(
  DEFAULT_LINE_OPACITY_FEATURE_ENTITY
);

export const DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY: Partial<HighlightedLineSizeFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      value: HIGHLIGHTED_WIDTH
    }
  };

export const generateHighlightedLineSizeFeatureEntity = createStateWithDefaults(
  DEFAULT_HIGHLIGHTED_LINE_SIZE_FEATURE_ENTITY
);

export const DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY: Partial<HighlightedLineOpacityFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      value: DEFAULT_OPACITY
    }
  };

export const generateHighlightedLineOpacityFeatureEntity = createStateWithDefaults(
  DEFAULT_HIGHLIGHTED_LINE_OPACITY_FEATURE_ENTITY
);

export const DEFAULT_LASSO_FEATURE_ENTITY: Partial<LassoFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    list: [],
    brushEnd: false,
    position: ORIGIN
  }
};

export const generateLassoFeatureEntity = createStateWithDefaults(
  DEFAULT_LASSO_FEATURE_ENTITY
);
export const DEFAULT_P10_FEATURE_ENTITY: Partial<StatisticsFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    opacity: DEFAULT_OPACITY
  }
};

export const generateP10FeatureEntity = createStateWithDefaults(
  DEFAULT_P10_FEATURE_ENTITY
);

export const DEFAULT_P50_FEATURE_ENTITY: Partial<StatisticsFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    opacity: DEFAULT_OPACITY
  }
};

export const generateP50FeatureEntity = createStateWithDefaults(
  DEFAULT_P50_FEATURE_ENTITY
);

export const DEFAULT_P90_FEATURE_ENTITY: Partial<StatisticsFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    opacity: DEFAULT_OPACITY
  }
};

export const generateP90FeatureEntity = createStateWithDefaults(
  DEFAULT_P90_FEATURE_ENTITY
);

export const DEFAULT_AVERAGE_FEATURE_ENTITY: Partial<StatisticsFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    opacity: DEFAULT_OPACITY
  }
};

export const generateAverageFeatureEntity = createStateWithDefaults(
  DEFAULT_AVERAGE_FEATURE_ENTITY
);

export const DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY: Partial<CalculationMethodFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      method: CalculationMethod.Count
    }
  };

export const generateCalculationMethodFeatureEntity = createStateWithDefaults(
  DEFAULT_CALCULATION_METHOD_FEATURE_ENTITY
);

export const DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY: Partial<ChartBaseFeature> = {
  ...DEFAULT_FEATURE_ENTITY
};

export const generateHistogramToggleFeatureEntity = createStateWithDefaults(
  DEFAULT_HISTOGRAM_TOGGLE_FEATURE_ENTITY
);

export const DEFAULT_JITTER_PLOT_FEATURE_ENTITY: Partial<JitterPlotToggleFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    xBinDefaulting: false,
    jitterBin: null
  }
};

export const DEFAULT_BEST_FIT_FEATURE_ENTITY: Partial<BestFitToggleFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    regressionType: RegressionType.Linear,
    color: BLACK_HEX,
    thickness: DEFAULT_LINE_THICKNESS,
    opacity: DEFAULT_OPACITY,
    forceRegressionThroughOrigin: false
  }
};

export const generateJitterPlotFeatureEntity = createStateWithDefaults(
  DEFAULT_JITTER_PLOT_FEATURE_ENTITY
);

// Both modes follow the focus bins until a chart or system dashboard turns them on.
export const DEFAULT_BOX_PLOT_BINS_FEATURE_ENTITY: Partial<BoxPlotBinsFeature> = {
  properties: {
    box: { active: false, maxBins: 10 },
    violin: { active: false, maxBins: 10 }
  }
};

export const DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY: Partial<ViolinPlotToggleFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {}
};

export const generateViolinPlotFeatureEntity = createStateWithDefaults(
  DEFAULT_VIOLIN_PLOT_FEATURE_ENTITY
);

export const DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY: Partial<DataRangeSelectFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    range: "all"
  }
};

export const generateDataRangeSelectFeatureEntity = createStateWithDefaults(
  DEFAULT_DATA_RANGE_SELECT_FEATURE_ENTITY
);

export const generateBestFitFeatureEntity = createStateWithDefaults(
  DEFAULT_BEST_FIT_FEATURE_ENTITY
);

export const DEFAULT_SCREENSHOT_FEATURE_ENTITY: Partial<ScreenshotFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    preset: undefined
  }
};

export const generateScreenshotFeatureEntity = createStateWithDefaults(
  DEFAULT_SCREENSHOT_FEATURE_ENTITY
);

export const DEFAULT_NORMALIZATION_ENTITY: Partial<NormalizationFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  enabled: true,
  properties: {
    displayName: DEFAULT_NORMALIZE_BY_DISPLAY_FIELD,
    field: DEFAULT_NORMALIZE_BY_FIELD,
    higherScalar: DEFAULT_NORMALIZE_BY_HIGHER_SCALER,
    lowerScalar: DEFAULT_NORMALIZE_BY_LOWER_SCALER,
    per: DEFAULT_NORMALIZE_BY_PER,
    threshold: DEFAULT_NORMALIZE_BY_THRESHOLD,
    unit: DEFAULT_NORMALIZE_BY_UNIT,
    defaultUnit: "m", // Original unit from custom fields (org-defined/user-defined)
    units: DEFAULT_NORMALIZATION_UNITS,
    useMultilinearNormalization: false,
    useNormalizeBy: false
  }
};

export const generateNormalizationFeatureEntity = createStateWithDefaults(
  DEFAULT_NORMALIZATION_ENTITY
);

export const DEFAULT_CHART_FOCUS_ENTITY: Partial<ChartFocusFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    // Declared so createMergeWithDefaults keeps it — getAllowedKeys builds its
    // allow-list from this object, and an undeclared key is dropped from a
    // restored dashboard's saved features. Left undefined, never false: false is
    // a real answer that would merge over every dashboard saved before the key
    // existed and read as "nothing picked".
    colorPaletteIsExplicit: undefined,
    colorPalette: {
      id: "-1",
      name: DEFAULT_COLOR_PALETTE_NAME,
      colorScale: [],
      reverse: false,
      shared: false,
      paletteType: 0,
      thickness: [],
      preferredColors: [],
      colors: []
    },
    groupBy: {
      title: "Resource Play",
      property: "Header.ResourcePlay",
      groupByField: "Header.ResourcePlay",
      pdenSource: EvaSource.Public,
      canBin: false,
      dataType: "Text",
      categoryId: 1,
      tooltip: "",
      display: "",
      entityKind: EntityKind.Well,
      performanceDateRange: undefined,
      bin: {
        BinSize: "",
        MinSize: "",
        MaxBins: "",
        MaxBinsSortOrder: "WellCount",
        // Declared so createMergeWithDefaults keeps a saved "Sum of…" field.
        MaxBinsSortField: undefined,
        GreaterThan: "",
        Quantile: {
          quantileType: "NumberOfQuantile",
          numQuantiles: 4
        },
        BinType: "BinSize",
        LessThan: "",
        UseDynamicBins: false
      }
    }
  }
};

export const generateChartFocusEntity = createStateWithDefaults(
  DEFAULT_CHART_FOCUS_ENTITY
);

export const DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY: Partial<MultiProductFocusFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    products: []
  }
};

export const generateMultiProductFocusFeatureEntity = createStateWithDefaults(
  DEFAULT_MULTI_PRODUCT_FOCUS_ENTITY
);

export const DEFAULT_CHART_FOCUS_MIDSTREAM_ENTITY: Partial<ChartFocusFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    // Declared for the same reason as the entity above.
    colorPaletteIsExplicit: undefined,
    colorPalette: {
      id: "-1",
      name: DEFAULT_COLOR_PALETTE_NAME,
      colorScale: [],
      reverse: false,
      shared: false,
      paletteType: 0,
      thickness: [],
      preferredColors: [],
      colors: []
    },
    groupBy: {
      title: "Facility Operator",
      property: "Header.OperatorName",
      groupByField: "Header.OperatorName",
      pdenSource: EvaSource.Public,
      canBin: false,
      dataType: "Text",
      categoryId: 1,
      tooltip: "",
      display: "",
      entityKind: EntityKind.Facility,
      performanceDateRange: undefined,
      bin: {
        BinSize: "",
        MinSize: "",
        MaxBins: "",
        GreaterThan: "",
        Quantile: {
          quantileType: "NumberOfQuantile",
          numQuantiles: 4
        },
        BinType: "BinSize",
        LessThan: "",
        UseDynamicBins: false,
        MaxBinsSortOrder: "WellCount",
        // Declared so createMergeWithDefaults keeps a saved "Sum of…" field.
        MaxBinsSortField: undefined
      }
    }
  }
};

export const generateMidstreamChartFocusEntity = createStateWithDefaults(
  DEFAULT_CHART_FOCUS_MIDSTREAM_ENTITY
);

export const DEFAULT_CHART_TYPE_PARAM_FEATURE: Partial<ChartTypeParamFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    x: {
      group: "",
      subgroup: "",
      product: "",
      property: "",
      title: "",
      defaultUnit: "",
      dataType: "Text",
      canBin: false,
      pdenSource: PdenSourceEnum.Public.toString()
    },
    y: {
      group: "",
      subgroup: "",
      product: "",
      property: "",
      title: "",
      defaultUnit: "",
      dataType: "Text",
      canBin: false,
      pdenSource: PdenSourceEnum.Public.toString()
    }
  }
};

export const generateChartTypeParamFeature = createStateWithDefaults(
  DEFAULT_CHART_TYPE_PARAM_FEATURE
);

export const DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY: Partial<PressureChartSettingsFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      product: ALL_PRODUCT_TYPES.Oil.key
    }
  };

export const generatePressureChartSettingsFeature = createStateWithDefaults(
  DEFAULT_PRESSURE_CHART_SETTINGS_ENTITY
);

export const DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY: Partial<TotalFluidChartSettingsFeature> =
  {
    ...DEFAULT_FEATURE_ENTITY,
    properties: {
      product: ALL_PRODUCT_TYPES.TotalFluid.key
    }
  };

export const generateTotalFluidChartSettingsFeature = createStateWithDefaults(
  DEFAULT_TOTAL_FLUID_CHART_SETTINGS_ENTITY
);

export const DEFAULT_LOG_AXIS_FEATURE_ENTITY: Partial<LogAxisFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    x: { active: false },
    y: { active: false }
  }
};

export const generateLogAxisFeature = createStateWithDefaults(
  DEFAULT_LOG_AXIS_FEATURE_ENTITY
);

export const DEFAULT_X_AXIS_SORT_ENTITY: Partial<XAxisSortFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    value: "desc"
  }
};

export const generateXAxisSortFeatureEntity = createStateWithDefaults(
  DEFAULT_X_AXIS_SORT_ENTITY
);

export const DEFAULT_MULTI_PRODUCT_ENTITY: Partial<MultiProductFeature> = {
  ...DEFAULT_FEATURE_ENTITY,
  properties: {
    otherProducts: [],
    primaryProducts: [],
    secondaryProducts: [],
    primaryUnits: [],
    secondaryUnits: [],
    combineSameUnitAxis: false,
    isMMcfAxisPositionBottom: false,
    isMBblAxisPositionBottom: false,
    productLineStyles: {},
    overrideLineStyles: {},
    showProductLegend: true,
    overrideBinColors: false
  }
};

export const generateMultiProductFeatureEntity = createStateWithDefaults(
  DEFAULT_MULTI_PRODUCT_ENTITY
);
